import API from "../../core/apiConfig";
import {
  downloadBlobResponse,
  handleBlobExportError,
} from "../../core/download";
import {
  normalizePaginatedResponse,
  withRowKeys,
} from "../../core/response";
import type {
  ConfirmImportRouterBasePayload,
  CreateRouterSalaryPayload,
  RouteBasePayload,
  RouteManagerRequestOptions,
  RouterSalaryPayload,
  SearchRouterBaseVersionParams,
  UpdateRouterBaseVersionPayload,
} from "./types";

type RouterSalaryListItem = {
  _id?: string;
  routeBase?: Record<string, unknown> & { _id?: string };
  salary?: Record<string, unknown>;
};

type RouterSalaryVersionRow = Record<string, unknown> & {
  _id?: string | null;
};

export const getAllRouterBase = async (
  params: Record<string, unknown>,
  options?: RouteManagerRequestOptions,
) => {
  const response = await API.get("/admin/routeBase", {
    params,
    silentLoading: options?.silentLoading ?? false,
  }).then((res) => res.data);

  if (response.success) {
    return normalizePaginatedResponse(response, "page");
  }
};

export const getAllRouterSalary = async (
  params: Record<string, unknown>,
  id: string | null,
) => {
  const response = await API.get(
    `/admin/routeBase/fixedRouteSalary?version=${id}`,
    { params },
  ).then((res) => res.data);

  if (response.success) {
    const data = response.data.map((item: RouterSalaryListItem) => ({
      ...item.routeBase,
      ...item.salary,
      routerId: item.routeBase?._id,
      _id: item._id,
    }));
    return normalizePaginatedResponse({ ...response, data }, "page");
  }
};

export const getAllRouterSalaryVersion = async (
  params: Record<string, unknown>,
) => {
  const response = await API.get(
    "/admin/routeBase/fixedRouteSalary/version",
    { params },
  ).then((res) => res.data);

  if (response.success) {
    return { data: withRowKeys<RouterSalaryVersionRow>(response.data) };
  }
};

export const postPreCheckImportRouterBase = async (file: Blob) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await API.post(
    "/admin/routeBase/import/pre-check",
    formData,
  ).then((res) => res.data);
  return response;
};

export const postConfirmImportRouterBase = async (
  payload: ConfirmImportRouterBasePayload,
) => {
  const response = await API.post("/admin/routeBase/import/confirm", payload);
  return response.data;
};

export const putUpdateRouterBase = async (
  payload: RouteBasePayload,
  id: string,
) => {
  const response = await API.put(`/admin/routeBase/${id}`, payload);
  return response.data;
};

export const postAddRouterBase = async (payload: RouteBasePayload) => {
  const response = await API.post("/admin/routeBase", payload);
  return response.data;
};

export const shortDeleteRouterBase = async (payload: { ids: string[] }) => {
  const response = await API.put("/admin/routeBase/delete", payload);
  return response.data;
};

export const putUpdateRouterSalary = async (
  payload: RouterSalaryPayload,
  id: string,
) => {
  const response = await API.put(
    `/admin/routeBase/fixedRouteSalary/${id}`,
    payload,
  );
  return response.data;
};

export const getExportVersion = async (id: string, name: string) => {
  try {
    const response = await API.get("/admin/routeBase/export", {
      params: { versionId: id },
      responseType: "blob",
      silent: true,
      silentLoading: true,
    });

    return downloadBlobResponse(response, `Mã tuyến ${name}.xlsx`);
  } catch (error: unknown) {
    await handleBlobExportError(error);
    throw error;
  }
};

export const postCreateSalary = async (payload: CreateRouterSalaryPayload) => {
  const response = await API.post(
    "/admin/routeBase/fixedRouteSalary",
    payload,
  );
  return response.data;
};

export const getOneRouter = async (id: string) => {
  const response = await API.get(`/admin/routeBase/${id}`);
  return response.data;
};

export const getSearchRouterBaseVersion = async (
  params: SearchRouterBaseVersionParams,
) => {
  const response = await API.get("/admin/routeBase/search", {
    params,
    silentLoading: true,
  }).then((res) => res.data);

  if (response.success) {
    return { data: withRowKeys(response.data) };
  }
};

export const putUpdateRouterBaseVersion = async (
  payload: UpdateRouterBaseVersionPayload,
  id: string,
) => {
  const response = await API.put(
    `/admin/routeBase/fixedRouteSalary/version/${id}`,
    payload,
  );
  return response.data;
};

export default {
  getAllRouterBase,
  postPreCheckImportRouterBase,
  postConfirmImportRouterBase,
  getAllRouterSalary,
  putUpdateRouterBase,
  postAddRouterBase,
  getAllRouterSalaryVersion,
  shortDeleteRouterBase,
  putUpdateRouterSalary,
  getExportVersion,
  postCreateSalary,
  getOneRouter,
  getSearchRouterBaseVersion,
  putUpdateRouterBaseVersion,
};
